/**
 * TrackGrid — the drag-free authoring path: one collapsible group per track,
 * each an InlineRowGrid over that track's PLAN bars. Add / edit (label, start,
 * end, depth, phase) / reorder (↑ ↓) / delete a plan step here and the visual
 * timeline reflects it, because both read the same node list. Actual/NPT overlay
 * bars are not listed — they aren't W's to author.
 *
 * This is why W7 is fully authorable even without pointer drag: the grid is the
 * canonical editor, the canvas the visual mirror (drag is a bonus on top).
 */
import type { TimelineNode, TimelineTrack } from "@api/types";
import type { MessageKey } from "@i18n/messages";
import { useLocale } from "@hooks/useLocale";
import { InlineRowGrid, type GridColumn } from "@components/input/InlineRowGrid";
import { isPlanNode } from "./timelineModel";

/** The bound translate fn `useLocale().t` (typed for reuse in the column defs). */
type T = (key: MessageKey, vars?: Record<string, string | number>) => string;

export interface TrackGridProps {
  tracks: TimelineTrack[];
  nodes: TimelineNode[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onPatch: (id: string, patch: Partial<TimelineNode>) => void;
  onReorder: (id: string, dir: -1 | 1) => void;
  onDelete: (id: string) => void;
  onAdd: (track: TimelineTrack) => void;
}

export function TrackGrid({
  tracks,
  nodes,
  selectedId,
  onSelect,
  onPatch,
  onReorder,
  onDelete,
  onAdd,
}: TrackGridProps) {
  const { t } = useLocale();

  return (
    <section className="tl-grids">
      <h2 className="tl-grids-h">{t("timeline.grid.title")}</h2>
      <p className="dim" style={{ fontSize: 12.5, margin: "0 0 12px" }}>
        {t("timeline.grid.hint")}
      </p>
      {tracks.map((track) => {
        const band = nodes
          .filter((n) => n.track_id === track.id && isPlanNode(n))
          .sort((a, b) => a.seq - b.seq);
        return (
          <div key={track.id} className="tl-track-block">
            <div className="tl-track-hd">
              <span className={"tl-kind kind-" + track.kind}>{t(`timeline.kind.${track.kind}` as MessageKey)}</span>
              <b>{track.label}</b>
              {track.sub && <span className="dim">· {track.sub}</span>}
              <button type="button" className="btn ghost sm" style={{ marginLeft: "auto" }} onClick={() => onAdd(track)}>
                <span className="ic ic-add" aria-hidden="true" />
                {t("timeline.addStep")}
              </button>
            </div>
            <InlineRowGrid<TimelineNode>
              rows={band}
              columns={columns(t, onSelect, onPatch, selectedId)}
              onChange={reconcile(band, onReorder, onDelete)}
              emptyLabel={t("timeline.trackEmpty")}
            />
          </div>
        );
      })}
    </section>
  );
}

/**
 * Bridge InlineRowGrid's row-level onChange (whole-array replace) to our
 * id-keyed reorder/delete handlers. InlineRowGrid only ever reorders (move up/
 * down = adjacent swap) or removes a row via this grid, so we diff the incoming
 * array against the previous band to fire the right id-keyed action.
 */
function reconcile(
  prev: TimelineNode[],
  onReorder: (id: string, dir: -1 | 1) => void,
  onDelete: (id: string) => void,
) {
  return (next: TimelineNode[]) => {
    if (next.length < prev.length) {
      const removed = prev.find((p) => !next.some((n) => n.id === p.id));
      if (removed) onDelete(removed.id);
      return;
    }
    // adjacent swap: find the first index whose id changed
    for (let i = 0; i < next.length; i += 1) {
      if (next[i]?.id !== prev[i]?.id) {
        const moved = next[i];
        if (moved) onReorder(moved.id, prev.findIndex((p) => p.id === moved.id) > i ? -1 : 1);
        return;
      }
    }
  };
}

function columns(
  t: T,
  onSelect: (id: string) => void,
  onPatch: (id: string, patch: Partial<TimelineNode>) => void,
  selectedId: string | null,
): GridColumn<TimelineNode>[] {
  return [
    {
      key: "seq",
      label: t("timeline.col.seq"),
      width: 52,
      render: (r) => (
        <button
          type="button"
          className={"seqnum-btn" + (r.id === selectedId ? " on" : "")}
          onClick={() => onSelect(r.id)}
          aria-label={t("timeline.selectRow")}
        >
          {r.seq}
        </button>
      ),
    },
    {
      key: "label",
      label: t("timeline.col.label"),
      render: (r) => (
        <input
          className="cell"
          value={r.label}
          aria-label={t("timeline.col.label")}
          placeholder={t("timeline.labelPlaceholder")}
          onChange={(e) => onPatch(r.id, { label: e.target.value })}
        />
      ),
    },
    {
      key: "start",
      label: t("timeline.col.start"),
      width: 150,
      render: (r) => (
        <input
          className="cell"
          type="date"
          value={r.start.slice(0, 10)}
          aria-label={t("timeline.col.start")}
          onChange={(e) => onPatch(r.id, { start: e.target.value })}
        />
      ),
    },
    {
      key: "end",
      label: t("timeline.col.end"),
      width: 150,
      render: (r) => (
        <input
          className="cell"
          type="date"
          value={r.end?.slice(0, 10) ?? ""}
          aria-label={t("timeline.col.end")}
          onChange={(e) => onPatch(r.id, { end: e.target.value || null })}
        />
      ),
    },
    {
      key: "phase",
      label: t("timeline.col.phase"),
      render: (r) => (
        <input
          className="cell"
          value={r.phase ?? ""}
          aria-label={t("timeline.col.phase")}
          placeholder={t("timeline.phasePlaceholder")}
          onChange={(e) => onPatch(r.id, { phase: e.target.value || null })}
        />
      ),
    },
  ];
}
